import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { AdminBentoCategory } from "schemas/admin";
import { BentoBrand, BentoBrandVerificationStatus } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

import NewBrand from "../Brand/NewBrand";
import styles from "./styles";

const AdminBrandVerifications = () => {
  const { getToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [results, setResults] = useState<BentoBrand[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const [categoriesById, setCategoriesById] = useState<{
    [key: number]: AdminBentoCategory;
  }>({});

  const [message, severity, setAlert, closeAlert] = useAlert();

  const navigate = useNavigate();

  const fetchPendingVerifications = async (pageToFetch: number) => {
    setLoading(true);

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-brands/verification-required?page=${pageToFetch}&per_page=${20}`,
        "GET",
      );
      setResults(res.bentoBrands);
      setTotalCount(res.total);
    } catch (error) {
      setAlert(error.message || "An error occurred", "warning");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-categories`,
        "GET",
      );
      const categoriesById: { [key: number]: AdminBentoCategory } = {};
      for (const category of res.bentoCategories) {
        categoriesById[category.id] = category;
      }
      setCategoriesById(categoriesById);
    } catch (error) {
      setAlert(
        "An error occurred, you do not have access to this page",
        "warning",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (e: CustomEvent, newPage: number) => {
    setPage(newPage + 1);
    fetchPendingVerifications(newPage + 1);
  };

  const normalizeUrl = (url: string) => {
    return url.startsWith("http") ? url : `https://${url}`;
  };

  useEffect(() => {
    fetchCategories();
    fetchPendingVerifications(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledPage
      content={
        <>
          <Backdrop sx={styles.backdrop} open={loading}>
            <CircularProgress />
          </Backdrop>
          <Grid container sx={{ mt: 2 }} spacing={1} alignItems="center">
            <Typography variant="h3" gutterBottom>
              <strong>Brand Verifications</strong>
            </Typography>
          </Grid>

          <TableContainer sx={styles.tableContainer}>
            <Table stickyHeader sx={styles.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Brand Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Website</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Instagram</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Priority</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Source</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Notes</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Reasons</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Comment</strong>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((bentoBrand) => (
                  <>
                    <TableRow
                      key={bentoBrand.id}
                      sx={{ backgroundColor: "#f5f5f5" }}
                    >
                      <TableCell>
                        <strong>
                          {bentoBrand.brandName} ({bentoBrand.id})
                        </strong>
                      </TableCell>
                      <TableCell>
                        <a
                          href={normalizeUrl(bentoBrand.website!)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {bentoBrand.website}
                        </a>
                      </TableCell>
                      <TableCell>
                        {bentoBrand.instagramUsername && (
                          <a
                            href={`https://instagram.com/${bentoBrand.instagramUsername}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {bentoBrand.instagramUsername}
                          </a>
                        )}
                      </TableCell>
                      <TableCell>
                        {bentoBrand.flags?.length === 0 &&
                          bentoBrand.verificationPriority}
                      </TableCell>
                      <TableCell />
                      <TableCell>{bentoBrand.verificationNotes}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell>
                        <Button
                          onClick={() => {
                            navigate(`${bentoBrand.id}`);
                          }}
                          variant="outlined"
                          size="small"
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                    {bentoBrand.flags?.map((flag) => (
                      <TableRow key={flag.id}>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>{flag.priority}</TableCell>
                        <TableCell>
                          {flag.source === "user"
                            ? `user (${flag.orgUserId})`
                            : flag.source}
                        </TableCell>
                        <TableCell>{flag.notes || "-"}</TableCell>
                        <TableCell>
                          {flag.submittedReasons?.join(", ") ||
                            flag.flaggedData?.join(", ") ||
                            "-"}
                        </TableCell>
                        <TableCell>{flag.submittedComment || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount || 0}
            rowsPerPage={20}
            rowsPerPageOptions={[20]}
            page={page - 1}
            onPageChange={handleChangePage}
          />
          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
          <Routes>
            <Route
              path="/:id"
              element={
                <NewBrand
                  brands={results}
                  setBrands={setResults}
                  categoriesById={categoriesById}
                  handleClose={() =>
                    navigate(`/admin/brand-verifications`, { replace: true })
                  }
                  postSaveAction={(brand) => {
                    if (
                      brand.verificationStatus !==
                      BentoBrandVerificationStatus.pending
                    ) {
                      setResults((prevBrands) =>
                        prevBrands.filter((b) => b.id !== brand.id),
                      );
                    }
                  }}
                />
              }
            />
          </Routes>
        </>
      }
    />
  );
};

export default AdminBrandVerifications;
